import { useRest } from "@/composables/useRest";

/**
 * Composable para manejar las llamadas API de datos legales
 */
export function useLegalDataApi() {
  const rest = useRest();

  /**
   * Actualiza el número de legajo/actuación
   * @param {string} actuacionId - ID de la actuación
   * @param {string} numero - Número completo (ej: "111123/25")
   * @param {object} datosComplementarios - Datos adicionales requeridos
   */
  async function updateNumero(actuacionId, numero, datosComplementarios) {
    const url = `/actuacion/${actuacionId}`;
    const formData = {
      Numero: numero,
      DenunciaID: datosComplementarios.DenunciaID,
      DependenciaID: datosComplementarios.DependenciaID,
      Fechainicio: datosComplementarios.Fechainicio,
      TiposumarioID: datosComplementarios.TiposumarioID,
    };

    return await rest.put({ url, formData });
  }

  /**
   * Actualiza las causas/carátulas
   * @param {string} actuacionId - ID de la actuación
   * @param {array} listaCaratulas - Array de causas/carátulas
   * @param {object} datosComplementarios - Datos adicionales requeridos
   */
  async function updateCausasCaratulas(actuacionId, listaCaratulas, datosComplementarios) {
    const url = `/actuacion/${actuacionId}`;
    const params = {
      method: "own",
      section: "listaCaratulas",
    };
    const formData = {
      id: actuacionId,
      listaCaratulas,
      DenunciaID: datosComplementarios.DenunciaID,
      DependenciaID: datosComplementarios.DependenciaID,
      Fechainicio: datosComplementarios.Fechainicio,
      TiposumarioID: datosComplementarios.TiposumarioID,
    };

    return await rest.put({ url, params, formData });
  }

  /**
   * Actualiza los artículos (para expedientes)
   * Compara artículos nuevos vs existentes y hace POST/DELETE según corresponda
   * @param {string} actuacionId - ID de la actuación
   * @param {array} nuevosArticulosIds - Array de IDs de artículos seleccionados
   * @param {array} articulosExistentes - Array de artículos actuales con ActuacionArticuloID
   */
  async function updateArticulos(actuacionId, nuevosArticulosIds, articulosExistentes) {
    const resultados = {
      agregados: [],
      eliminados: [],
      errores: [],
    };

    const articulosParaEliminar = [...articulosExistentes];

    for (const articuloId of nuevosArticulosIds) {
      const existe = articulosExistentes.find((art) => art.ArticulosID === articuloId);

      if (!existe) {
        try {
          const url = `/actuacion-articulo/`;
          const formData = {
            ActuacionID: actuacionId,
            ArticulosID: articuloId,
          };
          await rest.post({ url, formData });
          resultados.agregados.push(articuloId);
        } catch (error) {
          resultados.errores.push({ articuloId, error, accion: "agregar" });
        }
      } else {
        const index = articulosParaEliminar.findIndex((art) => art.ArticulosID === articuloId);
        if (index !== -1) {
          articulosParaEliminar.splice(index, 1);
        }
      }
    }

    for (const articulo of articulosParaEliminar) {
      try {
        const url = `/actuacion-articulo/${articulo.ActuacionArticuloID}`;
        await rest.del({ url });
        resultados.eliminados.push(articulo.ArticulosID);
      } catch (error) {
        resultados.errores.push({
          articuloId: articulo.ArticulosID,
          error,
          accion: "eliminar",
        });
      }
    }

    if (resultados.errores.length > 0) {
      throw new Error(
        `Error actualizando artículos: ${resultados.errores.length} operaciones fallidas`
      );
    }

    return resultados;
  }

  /**
   * Actualiza el juzgado interviniente
   * @param {string} roljuzgadoactuacionID - ID del rol juzgado actuación
   * @param {object} juzgadoData - Datos del juzgado (JuzgadoID, Nombre, Descripcion, ActuacionID)
   */
  async function updateJuzgado(roljuzgadoactuacionID, juzgadoData) {
    const url = `/parametros/rol-juzgado-actuacion/${roljuzgadoactuacionID}`;
    const formData = {
      ActuacionID: juzgadoData.ActuacionID,
      Descripcion: juzgadoData.Descripcion,
      Nombre: juzgadoData.Nombre,
      JuzgadoID: juzgadoData.JuzgadoID,
    };

    return await rest.put({ url, formData });
  }

  /**
   * Actualiza la UFI
   * @param {string} actuacionufiID - ID de ActuacionUfi
   * @param {string} ufiId - ID de la UFI (TipoufiID)
   * @param {string} actuacionId - ID de la actuación
   */
  async function updateUfi(actuacionufiID, ufiId, actuacionId) {
    const url = `/actuacionufi/${actuacionufiID}`;
    const formData = {
      TipoufiID: ufiId,
      ActuacionID: actuacionId,
    };

    return await rest.put({ url, formData });
  }

  /**
   * Actualiza el fiscal a cargo
   * @param {string} actuacionId - ID de la actuación
   * @param {object} fiscalData - Datos del fiscal con PersonalfiscalID
   */
  async function updateFiscal(actuacionId, fiscalData) {
    const url = `/actuacion/${actuacionId}`;
    const params = {
      method: "own",
      section: "fiscal",
    };

    const formData = {
      prosecutor: fiscalData,
    };

    return await rest.put({ url, params, formData });
  }

  /**
   * Actualiza el ayudante fiscal
   * @param {string} actuacionId - ID de la actuación
   * @param {object} ayudanteData - Datos del ayudante con PersonalfiscalID
   */
  async function updateAyudanteFiscal(actuacionId, ayudanteData) {
    const url = `/actuacion/${actuacionId}`;
    const params = {
      method: "own",
      section: "ayudanteFiscal",
    };

    const formData = {
      assistant: ayudanteData,
    };

    return await rest.put({ url, params, formData });
  }

  /**
   * Actualiza el modus operandi
   * @param {string} actuacionId - ID de la actuación
   * @param {object} modusOperandi - Objeto con Nombre y Descripcion del modus operandi
   */
  async function updateModusOperandi(actuacionId, modusOperandi) {
    const url = `/actuacion/${actuacionId}`;
    const params = {
      method: "own",
      section: "ModusOperandi",
    };
    const formData = {
      ModusOperandi: {
        Nombre: modusOperandi.Nombre,
        Descripcion: modusOperandi.Descripcion || null,
      },
    };

    return await rest.put({ url, params, formData });
  }

  /**
   * Actualiza el sitio
   * @param {string} actuacionId - ID de la actuación
   * @param {string} sitioId - ID del sitio
   */
  async function updateSitio(actuacionId, sitioId) {
    const url = `/actuacion/${actuacionId}`;
    const params = {
      method: "own",
      section: "Sitio",
    };
    const formData = {
      Sitio: {
        TipoSitioID: sitioId,
      },
    };

    return await rest.put({ url, params, formData });
  }

  return {
    updateNumero,
    updateCausasCaratulas,
    updateArticulos,
    updateJuzgado,
    updateUfi,
    updateFiscal,
    updateAyudanteFiscal,
    updateModusOperandi,
    updateSitio,
  };
}
